import React from 'react';
import { Sequence, AbsoluteFill, useCurrentFrame } from 'remotion';
import { ThemeSystemConfig } from '../../clipCreator/remotion/Components/Theme/Theme';
import { AspectRatio } from '../AspectRatio';
import { TimelineDefaults } from '../TimelineDefaults';
import { createDynamicSequence } from './base';
import { AnimationConfig, SoundConfig } from './animated';
import { AnimationHelpers } from '../helpers';
import { SequenceTimingHelper, AnimationState } from '../helpers/SequenceTimingHelper';
import { PositionedItem, PositionedItemImpl } from './positioned';
import { GridArea } from './grid';
import { Position, calculateGridPosition } from '../Utils';
import { TextMedia, TextMediaImpl } from '../media/text';
import { AdvancedTextEngine } from '../text/AdvancedTextEngine';
import { 
    TextStyle,
    AdvancedTextConfig, 
    TextPageData, 
    TextToken, 
    DEFAULT_TEXT_STYLE,
    FlexibleTextConfig,
    BasicTextStyle 
} from '../text/types';

export interface TextOverlayItem extends PositionedItem<TextMedia> {
    text: string;
    tokens?: TextToken[];
    style?: {
        fontSize?: number;
        fontFamily?: string;
        color?: string;
        textAlign?: 'left' | 'center' | 'right';
        backgroundColor?: string;
        padding?: number;
        borderRadius?: number;
    };
    // Shared text styling options - used by both TextOverlay and Caption systems
    textStyle?: Partial<TextStyle>;
    // OPTIONAL animation properties - only use when needed
    entranceAnimation?: AnimationConfig;
    exitAnimation?: AnimationConfig;
    entranceSoundEffect?: SoundConfig;
    exitSoundEffect?: SoundConfig;
    customAnimations?: AnimationConfig[];
    customSoundEffects?: SoundConfig[];
}

export class TextOverlayItemImpl extends PositionedItemImpl<TextMedia> implements TextOverlayItem {
    text: string;
    tokens?: TextToken[];
    // OPTIONAL animation properties - only set when specified
    entranceAnimation?: AnimationConfig;
    exitAnimation?: AnimationConfig;
    entranceSoundEffect?: SoundConfig;
    exitSoundEffect?: SoundConfig;
    customAnimations?: AnimationConfig[];
    customSoundEffects?: SoundConfig[];
    style: {
        fontSize: number;
        fontFamily: string;
        color: string;
        textAlign: 'left' | 'center' | 'right';
        backgroundColor: string;
        padding: number;
        borderRadius: number;
    };
    textStyle?: Partial<TextStyle>;

    constructor(textOverlay: Omit<TextOverlayItem, 'render' | 'mediaObject'>) {
        // Create a text media object for the text overlay
        const textMedia = new TextMediaImpl(textOverlay.id + '-text', 'overlay', textOverlay.text);
        // Apply hybrid positioning: use position OR gridArea, not both
        // Default to Position.Center if neither is provided
        const finalPosition = textOverlay.gridArea ? undefined : (textOverlay.position ?? Position.Center);
        const finalGridArea = textOverlay.position || (!textOverlay.gridArea && !textOverlay.position) 
            ? undefined 
            : (textOverlay.gridArea ?? { row: 2, col: 2, rows: 2, cols: 2, gridSize: 5 });
        
        super({ 
            ...textOverlay, 
            mediaObject: textMedia,
            gridArea: finalGridArea,
            position: finalPosition
        });
        
        this.text = textOverlay.text;
        this.tokens = textOverlay.tokens;
        
        // Only set animation properties if they're provided
        this.entranceAnimation = textOverlay.entranceAnimation;
        this.exitAnimation = textOverlay.exitAnimation;
        this.entranceSoundEffect = textOverlay.entranceSoundEffect;
        this.exitSoundEffect = textOverlay.exitSoundEffect;
        this.customAnimations = textOverlay.customAnimations ?? [];
        this.customSoundEffects = textOverlay.customSoundEffects ?? [];
        this.style = {
            fontSize: textOverlay.style?.fontSize ?? 24,
            fontFamily: textOverlay.style?.fontFamily ?? 'Arial',
            color: textOverlay.style?.color ?? '#ffffff',
            textAlign: textOverlay.style?.textAlign ?? 'center',
            backgroundColor: textOverlay.style?.backgroundColor ?? 'rgba(0, 0, 0, 0.7)',
            padding: textOverlay.style?.padding ?? 20,
            borderRadius: textOverlay.style?.borderRadius ?? 10,
        };
        
        // Initialize shared text style if provided
        this.textStyle = textOverlay.textStyle;
    }

    /**
     * Play sound effect (placeholder - integrate with audio system)
     */
    playSoundEffect(soundConfig: SoundConfig): void {
        console.log(`Playing sound effect: ${soundConfig.sound} at volume ${soundConfig.timing?.volume ?? 1}`);
    }

    render(fps: number, theme: ThemeSystemConfig, aspectRatio: AspectRatio, defaults: TimelineDefaults, videoConfig?: { width: number; height: number }): React.ReactNode {
        const hasAdvancedFeatures = this.tokens || this.textStyle?.textAnimation !== 'none' || this.textStyle?.outline !== 'none';
        const hasAnimations = this.entranceAnimation || this.exitAnimation;
        
        if (hasAdvancedFeatures || hasAnimations) {
            // ADVANCED TEXT OVERLAY: Use sophisticated text and animation system
            const { startFrame, durationFrames } = SequenceTimingHelper.calculateSequenceTiming(
                this.startAt,
                this.duration,
                fps,
                this.entranceAnimation,
                this.exitAnimation
            );
            
            // Create descriptive sequence name with advanced text info
            let sequenceName = SequenceTimingHelper.createSequenceName(
                'TextOverlay',
                this.id,
                this.startAt,
                this.duration,
                this.entranceAnimation,
                this.exitAnimation
            );
            
            // Add advanced text info to the name if present
            if (this.textStyle?.textAnimation !== 'none') {
                sequenceName = sequenceName.replace('[', `[textStyle:${this.textStyle?.textAnimation}|`);
            }
            
            const sequenceProps = {
                key: this.id,
                name: sequenceName,
                from: startFrame,
                durationInFrames: durationFrames
            };

            return (
                <Sequence {...sequenceProps}>
                    <AbsoluteFill>
                        <AdvancedTextOverlayContent
                            textOverlay={this}
                            fps={fps}
                            theme={theme}
                            aspectRatio={aspectRatio}
                            defaults={defaults}
                            videoConfig={videoConfig}
                        />
                    </AbsoluteFill>
                </Sequence>
            );
        } else {
            // BASIC TEXT OVERLAY: Use simple, direct rendering like basic captions
            const startFrame = this.startAt * fps;
            const durationFrames = (this.duration || 0) * fps;
            
            // Create descriptive sequence name using centralized helper
            const sequenceName = SequenceTimingHelper.createSequenceName(
                'TextOverlay',
                this.id,
                this.startAt,
                this.duration,
                undefined, // no entrance animation
                undefined, // no exit animation
                true // isBasic flag
            );

            const sequenceProps = {
                key: this.id,
                name: sequenceName,
                from: startFrame,
                durationInFrames: durationFrames
            };

            return (
                <Sequence {...sequenceProps}>
                    <AbsoluteFill>
                        <BasicTextOverlayContent
                            textOverlay={this}
                            fps={fps}
                            theme={theme}
                            aspectRatio={aspectRatio}
                            defaults={defaults}
                            videoConfig={videoConfig}
                        />
                    </AbsoluteFill>
                </Sequence>
            );
        }
    }
}

/**
 * BasicTextOverlayContent component handles simple text overlays with grid positioning
 * Uses the timeloom 5x5 grid system for consistent positioning
 */
const BasicTextOverlayContent: React.FC<{
    textOverlay: TextOverlayItemImpl;
    fps: number;
    theme: ThemeSystemConfig;
    aspectRatio: AspectRatio;
    defaults: TimelineDefaults;
    videoConfig?: { width: number; height: number };
}> = ({ textOverlay, aspectRatio, defaults, videoConfig }) => {
    // Calculate grid position using timeloom's positioning system
    const position = calculateGridPosition(
        textOverlay.gridArea,
        textOverlay.scale ?? 1,  // Default to 1 if undefined
        textOverlay.layoutPadding ?? defaults?.layoutPadding ?? 0.05,
        textOverlay.paddingMode ?? 'container',
        textOverlay.position ?? Position.Center
    );
    
    return (
        <div
            style={{
                position: 'absolute',
                ...position,
                fontSize: textOverlay.style.fontSize,
                fontFamily: textOverlay.style.fontFamily,
                color: textOverlay.style.color,
                backgroundColor: textOverlay.style.backgroundColor,
                padding: textOverlay.style.padding,
                borderRadius: textOverlay.style.borderRadius,
                textAlign: textOverlay.style.textAlign,
                display: 'flex',
                alignItems: 'center',
                justifyContent: textOverlay.style.textAlign === 'left' ? 'flex-start' : 
                               textOverlay.style.textAlign === 'right' ? 'flex-end' : 'center',
                opacity: textOverlay.opacity
            }}
        >
            {textOverlay.text}
        </div>
    );
};

/**
 * AdvancedTextOverlayContent component handles sophisticated text overlay features
 * Uses the professional AdvancedTextEngine for enhanced text rendering
 */
const AdvancedTextOverlayContent: React.FC<{
    textOverlay: TextOverlayItemImpl;
    fps: number;
    theme: ThemeSystemConfig;
    aspectRatio: AspectRatio;
    defaults: TimelineDefaults;
    videoConfig?: { width: number; height: number };
}> = ({ textOverlay, fps, aspectRatio, defaults, videoConfig }) => {
    const currentFrame = useCurrentFrame();
    
    // Use centralized sequence animation styles helper
    const { animationStyles, visibility, animationState } = SequenceTimingHelper.getSequenceAnimationStyles(
        currentFrame,
        fps,
        textOverlay.duration,
        textOverlay.entranceAnimation,
        textOverlay.exitAnimation,
        {}, // No base transform for advanced text overlays
        textOverlay.opacity ?? 1
    );

    // Avoid DOM stacking during inactive windows (pre-roll/post-roll)
    if (animationState === AnimationState.INACTIVE) {
        return null;
    }
    
    // Calculate grid position using timeloom's positioning system
    const position = calculateGridPosition(
        textOverlay.gridArea,
        textOverlay.scale ?? 1,  // Default to 1 if undefined
        textOverlay.layoutPadding ?? defaults?.layoutPadding ?? 0.05,
        textOverlay.paddingMode ?? 'container',
        textOverlay.position ?? Position.Center
    );
    
    // FIXED: Extract CSS animation classes from animation styles
    // The CSS animation system returns special properties that need to be applied as classes
    const cssAnimationClass = (animationStyles as any).animationClass;
    const cssAnimationPrefix = (animationStyles as any).animationPrefix || 'animate__';
    
    // Create clean CSS properties without the special class properties
    const cleanAnimationStyles = { ...animationStyles };
    delete (cleanAnimationStyles as any).animationClass;
    delete (cleanAnimationStyles as any).animationName;
    delete (cleanAnimationStyles as any).animationPrefix;
    
    const finalContainerStyles = {
        position: 'absolute' as const,
        ...position,
        display: 'flex',
        alignItems: 'center',
        justifyContent: textOverlay.style.textAlign === 'left' ? 'flex-start' : 
                       textOverlay.style.textAlign === 'right' ? 'flex-end' : 'center',
        opacity: textOverlay.opacity,
        ...cleanAnimationStyles,
        ...visibility
    };
    
    // Build className for CSS animations
    let className = '';
    if (cssAnimationClass) {
        // Apply the base animate.css class and the specific animation class
        className = `${cssAnimationPrefix}animated ${cssAnimationClass}`;
        console.log(`[CSS_ANIMATION_FIX] TextOverlay ${textOverlay.id}: Applying CSS classes: ${className}`);
    }
    
    // Check if advanced text features are enabled
    if (textOverlay.textStyle) {
        // Generate default tokens if none provided
        const tokens = textOverlay.tokens || generateDefaultTextOverlayTokens(
            textOverlay.text, 
            textOverlay.startAt, 
            textOverlay.duration || 5
        );
        
        // No conversion needed - tokens are already TextToken type
        const textTokens: TextToken[] = tokens;
        
        // Create page data for AdvancedTextEngine
        const pageData: TextPageData = {
            text: textOverlay.text,
            tokens: textTokens,
            startMs: textOverlay.startAt * 1000
        };
        
        // Convert text overlay configuration to TextStyle with defaults
        const textStyleConfig: TextStyle = {
            primaryColor: textOverlay.textStyle.primaryColor ?? DEFAULT_TEXT_STYLE.primaryColor,
            currentSpokenWordColor: textOverlay.textStyle.currentSpokenWordColor ?? DEFAULT_TEXT_STYLE.currentSpokenWordColor,
            fontFamily: textOverlay.textStyle.fontFamily ?? textOverlay.style.fontFamily,
            maxFontSize: textOverlay.textStyle.maxFontSize ?? DEFAULT_TEXT_STYLE.maxFontSize,
            outline: textOverlay.textStyle.outline ?? DEFAULT_TEXT_STYLE.outline,
            background: textOverlay.textStyle.background ?? DEFAULT_TEXT_STYLE.background,
            textAnimation: textOverlay.textStyle.textAnimation ?? DEFAULT_TEXT_STYLE.textAnimation,
            borderRadius: textOverlay.textStyle.borderRadius ?? DEFAULT_TEXT_STYLE.borderRadius,
            captionCase: textOverlay.textStyle.captionCase ?? DEFAULT_TEXT_STYLE.captionCase,
            hidePunctuation: textOverlay.textStyle.hidePunctuation ?? DEFAULT_TEXT_STYLE.hidePunctuation,
            containerPadding: textOverlay.textStyle.containerPadding ?? DEFAULT_TEXT_STYLE.containerPadding,
            boxPadding: textOverlay.textStyle.boxPadding ?? DEFAULT_TEXT_STYLE.boxPadding
        };
        
        return (
            <div style={finalContainerStyles} className={className}>
                <AdvancedTextEngine
                    page={pageData}
                    config={textStyleConfig}
                    videoConfig={{ fps: fps, width: videoConfig?.width || 1920, height: videoConfig?.height || 1080 }}
                    currentFrame={currentFrame}
                    position={getTextOverlayPositionHint(textOverlay.position, textOverlay.gridArea)}
                    availableWidth={position.availableWidth}
                    availableHeight={position.availableHeight}
                />
            </div>
        );
    }
    
    // Fallback to basic text rendering for non-advanced text overlays
    return (
        <div style={finalContainerStyles} className={className}>
            <div
                style={{
                    fontSize: textOverlay.style.fontSize,
                    fontFamily: textOverlay.style.fontFamily,
                    color: textOverlay.style.color,
                    textAlign: textOverlay.style.textAlign,
                    backgroundColor: textOverlay.style.backgroundColor,
                    padding: textOverlay.style.padding,
                    borderRadius: textOverlay.style.borderRadius,
                }}
            >
                {textOverlay.text}
            </div>
        </div>
    );
};

/**
 * Helper function to determine position hint for AdvancedTextEngine from text overlay
 */
function getTextOverlayPositionHint(position?: Position, gridArea?: GridArea): 'center' | 'top' | 'bottom' {
    if (position) {
        if (position === Position.TopLeft || position === Position.TopCenter || position === Position.TopRight) {
            return 'top';
        }
        if (position === Position.BottomLeft || position === Position.BottomCenter || position === Position.BottomRight) {
            return 'bottom';
        }
    }
    
    if (gridArea) {
        // Map grid positions to hints
        if (gridArea.row === 1) return 'top';
        if (gridArea.row >= 4) return 'bottom';
    }
    
    return 'center'; // Default
}

/**
 * Generate default tokens for text overlay - creates a single token for the entire duration
 * This differs from captions which have word-by-word timing
 */
function generateDefaultTextOverlayTokens(text: string, startAt: number, duration: number): TextToken[] {
    const startMs = startAt * 1000;
    const endMs = startMs + (duration * 1000);
    
    // Text overlays display the entire text at once for the full duration
    return [{
        text: text,
        fromMs: startMs,
        toMs: endMs,
        index: 0
    }];
}

// Export components for external use
export { AdvancedTextOverlayContent, BasicTextOverlayContent };